// Mirror of crates/ferro-replay/src/lib.rs. The Rust crate documents the
// byte layout; keep both in step (FORMAT_VERSION guards mismatches).

export const MAGIC = "FERRORPL";
export const FORMAT_VERSION = 1;
export const NO_ID = 0xffffffff;
export const POS_ABSOLUTE = 255;

export const Field = {
  STATIC: 1,
  POS: 2,
  HP: 4,
  AMMO: 8,
  CARGO: 16,
  STATUS: 32,
  TARGET: 64,
  ALL: 127,
} as const;

export const UnitFlags = { CONVOY: 1, RETURNING: 2, ROAD: 4, ENGINE_OUT: 8, WEAPON_OUT: 16 } as const;
export const TrainFlags = { TROOPS: 1, OUTBOUND: 2, FREIGHT: 4 } as const;

/** An industry site (mine or plant), from the header. */
export interface SiteInfo {
  tile: number;
  faction: number;
  /** Index of the depot (station) whose inventory it works from. */
  station: number;
  kind: "mine" | "plant";
  name: string;
  full: number;
  operational_pct: number;
  /** A plant's lines (each run makes its recipe that many times); a mine's
   *  shafts. Absent or 0 before v29.46. */
  lines?: number;
}

/** A site's state in a frame. */
export interface SiteState {
  integrity: number;
  /** Mines: units extracted; plants: cycles run. */
  output: number;
  /** Mines: reserve left, in thousands. */
  reserve: number;
}

/** Axial neighbour offsets, in the simulation's direction order (hex::DIRS). */
export const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

export const Terrain = { OPEN: 0, ROUGH: 1, WATER: 2, BLOCKED: 3, FOREST: 4, FORD: 5, BRIDGE: 6 } as const;
/** Rough is generated as hills and Blocked as mountain crests, so they're named that way. */
export const TERRAIN_NAMES = ["Open", "Hills", "Water", "Mountains", "Forest", "Ford", "Bridge"];

export interface Blob {
  offset: number;
  len: number;
}

export interface Chunk {
  blob: Blob;
  first_frame: number;
  frames: number;
}

export interface FactionInfo {
  name: string;
  color: string;
}

/** A design's module, for icons built from parts. */
export interface ModuleInfo {
  kind: "weapon" | "armor" | "sensor" | "cargo" | "mortar" | string;
  pen?: number;
}

export interface DesignInfo {
  name: string;
  /** Frames carry hp in 16 bits, in units of this many (titans); 1 if absent. */
  hp_unit?: number;
  /** Titans: the footprint drawn hex by hex, and its legs. */
  footprint?: { tiles: [number, number][]; legs: { at: [number, number]; kind: string }[] } | null;
  /** The chassis ("Foot", "Robot", "Wheeled", "Tracked", "Towed") and modules (absent
   *  in older replays). */
  chassis?: string;
  modules?: ModuleInfo[];
  size: number;
  hp: number;
  speed: number;
  range: number;
  sight: number;
  damage: number;
  ammo: number;
  cargo: number;
  indirect: boolean;
  flight?: number;
  carrier: boolean;
  /** Armour and penetration (absent in older replays: none). */
  armor?: number;
  pen?: number;
  /** Build time, percent of an infantryman's; 0 or absent: not producible. */
  build_pct?: number;
  /** Infantry's body ("Robot", a species), if not a plain human's. */
  body?: string;
}

export interface Summary {
  alive: number[][];
  in_contact: number[][];
  dry_in_contact: number[][];
  suppressed: number[][];
  deaths: number[][];
  shells_in_flight: number[];
}

export interface Header {
  /** Simulation version that recorded the replay (absent in early files). */
  sim_version?: string;
  title: string;
  description: string;
  world: {
    width: number;
    height: number;
    zone_size: number;
    /** How the world has grown (the endless map), oldest first: each step's
     *  size in zones before and after, and where the old map lies in the
     *  new one (hexes). */
    growth?: { from: [number, number]; to: [number, number]; at: [number, number] }[];
  };
  /** The storm wall's breaches so far (where each storm breaker stood),
   *  oldest first; absent in worlds without a wall to breach. */
  storm?: { breaches: [number, number][] };
  tick_rate: number;
  frame_interval: number;
  frames: number;
  first_tick: number;
  factions: FactionInfo[];
  designs: DesignInfo[];
  unit_ids: number;
  summary: Summary;
  static_blob: Blob;
  chunks: Chunk[];
  /** Industry sites (absent in older replays); frames carry their state. */
  sites?: SiteInfo[];
  /** Freight good names, in the order frames list them. */
  freight_goods?: string[];
  /** What each producer makes at the first frame (design ids). */
  producer_designs?: number[];
  /** The depot each producer pays from (4294967295: free); absent when
   *  production is free. */
  producer_stations?: number[];
  /** Barracks frames carry recruits too (v28 on). */
  recruits?: boolean;
  /** A fogged live stream: the faction whose sight it shows. Deltas then
   *  list, after the removed units, those that only left sight. */
  fog?: number;
}

/** Producers that pay for their units, in header order (frame order). */
export function paying(h: Header): number[] {
  return (h.producer_stations ?? []).flatMap((s, p) => (s !== 0xffffffff ? [p] : []));
}

/** The distinct stations of a header's sites, ascending (frame order). */
export function stations(h: Header): number[] {
  return [...new Set((h.sites ?? []).map((s) => s.station))].sort((a, b) => a - b);
}

export interface Depot {
  tile: number;
  faction: number;
  stock: number;
  capacity: number;
  field: boolean;
  /** What it is (`DepotKind`; recordings before v29.46: 1 for any depot
   *  added during the run, else 0). */
  kind: DepotKind;
}

/** A depot's kind, as frames carry it (ferro-replay `DEPOT_*`). */
export const enum DepotKind {
  Depot = 0,
  Field = 1,
  Home = 2,
  Station = 3,
  Mine = 4,
  Plant = 5,
  Barracks = 6,
}

export interface Train {
  id: number;
  tile: number;
  faction: number;
  flags: number;
}

export interface Shell {
  from: number;
  tile: number;
  impact: number;
}
